import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { Button } from 'structure/home/components/Button';
import { HeaderContainer } from 'structure/Layouts/SFILayouts/BaseLayout/components/HeaderContainer';
import { ModalMenu } from 'structure/Layouts/SFILayouts/BaseLayout/components/ModalMenu';
import { HeaderTopNav } from './HeaderTopNav';
import { headerStatus } from 'interfaces/header-status.enum';
import { Body3, habenGrotesk, Tiny1 } from 'components/Typography';
import { LaunchpadButton } from 'components/LaunchpadButton';
import { useTranslation } from 'react-i18next';
import { getStaticAssetUrl } from 'utils/url';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { AccountInfo } from 'structure/Layouts/LayoutWithSideBar/AccountInfo';
import { ConnectButton } from 'components/ConnectButton';
import { useConnection } from 'wagmi';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { Header4 } from 'structure/home/components/Typography';
import { TeamImage } from 'structure/home/components/TeamImage';

interface MenuItem {
  title: string;
  href: string;
  className?: string;
  visible?: boolean;
  status?: string;
  image?: () => JSX.Element;
}

const MENU: MenuItem[] = [
  {
    title: 'Vaults',
    href: '/vaults',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
  },
  {
    title: 'Staking',
    href: '/staking',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
  },
  {
    title: 'Bridge',
    href: '/bridge',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
  },
  {
    title: 'Docs',
    href: '/docs',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
  },
];

const MOBILE_MENU: MenuItem[] = [
  {
    title: 'Vaults',
    href: '/vaults',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
    image: () => (
      <div className='rounded-full bg-[#EDFF6D] p-2'>
        <Icon icon='vault_dark' height={24} width={24} color='currentColor' />
      </div>
    ),
  },
  {
    title: 'Staking',
    href: '/staking',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
    image: () => (
      <div className='rounded-full bg-[#EDFF6D] p-2'>
        <Icon icon='coins_dark' height={24} width={24} color='currentColor' />
      </div>
    ),
  },
  {
    title: 'Bridge',
    href: '/bridge',
    className: `gap-2 text-[1rem] ${habenGrotesk.className} font-bold`,
    visible: true,
    image: () => (
      <div className='rounded-full bg-[#EDFF6D] p-2'>
        <Icon icon='bridge_dark' height={24} width={24} color='currentColor' />
      </div>
    ),
  },
  {
    title: 'Migrate NOW',
    href: '/migrate',
    visible: true,
  },
  {
    title: 'AI assistant',
    href: '/chat',
    visible: true,
  },
  {
    title: 'Docs',
    href: '/docs',
    visible: true,
  },
  {
    title: 'Testnet',
    href: '/testnet',
    visible: true,
    status: headerStatus.ENDED,
  },
];

export const HeaderHomePage = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation('common');
  if (isMobile) {
    return <HomePageHeaderMobile />;
  }
  return (
    <HeaderContainer
      className={`mt-6 flex flex-row items-center justify-between`}
    >
      <div className='flex flex-row items-center gap-2 xl:gap-6'>
        <Image
          className='h-[70px]'
          alt='Singularity Finance'
          src={getStaticAssetUrl(
            '/sfi/static/logo/singularity_finance_full_logo.svg'
          )}
          width={209}
          height={70}
        />
        <div className='flex flex-row justify-between gap-2 xl:gap-6'>
          <div className='flex flex-row items-center gap-2'>
            <Body3 className='font-normal text-white'>Founding Teams</Body3>
            <div className='flex flex-row gap-2'>
              <TeamImage
                className='h-[32px] w-[32px] p-1.5 md:h-[32px] md:w-[32px] lg:p-1.5'
                src={getStaticAssetUrl('/sfi/static/homepage/sdao.svg')}
                alt='singularitydao'
                width={20}
                height={20}
              />
              <TeamImage
                className='h-[32px] w-[32px] p-2 md:h-[32px] md:w-[32px] lg:p-2'
                src={getStaticAssetUrl('/sfi/static/homepage/cgv.svg')}
                alt='cogito'
                width={20}
                height={20}
              />
              <TeamImage
                className='h-[32px] w-[32px] p-2 md:h-[32px] md:w-[32px] lg:p-2'
                src={getStaticAssetUrl('/sfi/static/homepage/selfkey.svg')}
                alt='selfkey'
                width={20}
                height={20}
              />
            </div>
          </div>

          <div className='flex flex-row items-center gap-2'>
            <Body3 className='font-normal text-white'>Partner</Body3>
            <div className='flex h-[32px] justify-center rounded-full bg-[#272727] p-1'>
              <Image
                src={getStaticAssetUrl(
                  '/sfi/static/homepage/singularitynet.svg'
                )}
                className='h-full w-auto'
                alt='singularitynet'
                width='154'
                height='54'
              />
            </div>
          </div>
        </div>
      </div>
      <div className='flex gap-6 xl:gap-[2.5rem]'>
        <DesktopMenu />
        <LaunchpadButton label={t('LAUNCH_DAPP')} href='/capital_manager' />
      </div>
    </HeaderContainer>
  );
};

const DesktopMenu = () => {
  const { t } = useTranslation('common');
  return (
    <div className='ml-auto flex flex-row gap-6 xl:gap-[2.5rem]'>
      {MENU.filter((item) => item.visible).map((item, index) => (
        <Button
          key={index}
          className={`flex gap-1 p-0 text-base ${item.status === headerStatus.COMING_SOON && 'pointer-events-none'}`}
          style='link'
          size='unspecified'
          component={Link}
          href={item.href}
        >
          {item.title}{' '}
          {item.status && (
            <Tiny1
              className={
                item.status === headerStatus.ENDED
                  ? 'text-negative-800'
                  : 'text-positive-800'
              }
            >
              {' '}
              {item.status === headerStatus.ENDED
                ? t('LINK_STATUS.' + headerStatus.ENDED)
                : t('LINK_STATUS.' + headerStatus.LIVE)}
            </Tiny1>
          )}
        </Button>
      ))}
    </div>
  );
};

const HomePageHeaderMobile = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('common');
  const handleOpenChange = (open: boolean) => {
    setOpen(open);
  };
  const csr = useClientRenderDelay();
  const connection = useConnection();

  return (
    <div
      className={`relative flex w-full items-center justify-between px-5 pt-2`}
    >
      <HeaderTopNav open={open} onOpenChange={handleOpenChange} />
      <ModalMenu open={open} onOpenChange={handleOpenChange}>
        <div className='flex w-full flex-row items-start justify-between p-5 pt-6'>
          <HeaderTopNav open={open} onOpenChange={handleOpenChange} />
        </div>
        <div className='flex flex-col items-start gap-6 px-6'>
          {MOBILE_MENU.filter((item) => item.visible).map((item, index) => (
            <Button
              key={index}
              className={`flex gap-1 p-0 text-base ${item.className} ${item.status === headerStatus.COMING_SOON && 'pointer-events-none'}`}
              style='link'
              component={Link}
              href={item.href}
            >
              {item.image && item.image()}
              {item.title}
              {item.status && (
                <Tiny1
                  className={
                    item.status === headerStatus.ENDED
                      ? 'text-negative-800'
                      : 'text-positive-800'
                  }
                >
                  {' '}
                  {item.status === headerStatus.ENDED
                    ? t('LINK_STATUS.' + headerStatus.ENDED)
                    : t('LINK_STATUS.' + headerStatus.LIVE)}
                </Tiny1>
              )}
            </Button>
          ))}
        </div>
        <div className='absolute bottom-3 flex w-full flex-col items-center justify-center gap-6 py-3'>
          <div className='mt-auto flex w-full flex-row justify-between gap-6 px-6'>
            <div className='flex basis-1/2 flex-col items-start gap-4'>
              <Header4 className='font-normal text-[#A19E99]'>
                Founding Teams
              </Header4>
              <div className='flex flex-row gap-3'>
                <TeamImage
                  src={getStaticAssetUrl('/sfi/static/homepage/sdao.svg')}
                  alt='singularitydao'
                  width={60}
                  height={60}
                />
                <TeamImage
                  src={getStaticAssetUrl('/sfi/static/homepage/cgv.svg')}
                  alt='cogito'
                  width={60}
                  height={60}
                />
                <TeamImage
                  src={getStaticAssetUrl('/sfi/static/homepage/selfkey.svg')}
                  alt='selfkey'
                  width={60}
                  height={60}
                />
              </div>
            </div>
            <div className='flex flex-col items-start gap-4'>
              <Header4 className='font-normal text-[#A19E99]'>Partner</Header4>
              <div className='flex flex-row gap-6'>
                <div className='flex h-[43px] w-[113px] justify-center rounded-full bg-[#272727] p-2'>
                  <Image
                    src={getStaticAssetUrl(
                      '/sfi/static/homepage/singularitynet.svg'
                    )}
                    alt='singularitynet'
                    width={154}
                    height={54}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='relative top-[10px] flex basis-1/3 justify-center'>
            {connection.isConnected && csr ? (
              <AccountInfo button={{ className: 'bg-[#000000]' }} />
            ) : (
              <ConnectButton className='text-nowrap' />
            )}
          </div>
        </div>
      </ModalMenu>
    </div>
  );
};
